import { useRef, useState, useEffect } from "react";
import { ReactSVG } from "react-svg";
import Button from "./Button";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import NewToolTip from "./New-ToolTip";
import {
  capitalizeCustomText,
  formatToYYYY_MMM_DD,
} from "../../helpers/functions";
import {
  ClinicalServiceTypes,
  claimInfo,
  minorAilments,
} from "../patient/text/data";
import { useSelector } from "react-redux";
import GuidelineUpdate from "./tooltip/guideline-update";
import MedsCheck from "./tooltip/medication-review/medsCheck";
import Clinical from "./tooltip/common/clinical-inf";
import Alert from "../common/Alert";
import CustomInput from "./CustomInput";
import { ARBillingOptions, AMRBillingOptions } from "../patient/clinical-services/service-steps/education/data";
import { UnexpectedResponseException } from "pdfjs-dist";

const getLabelText = (label) => {
  if (typeof label === "string" || typeof label === "number") {
    return String(label);
  }

  if (Array.isArray(label)) {
    return label.map(getLabelText).join(" ");
  }

  if (label && typeof label === "object" && "props" in label) {
    return getLabelText(label.props?.children);
  }

  return "";
};

const AccordionItem = ({
  faq,
  id,
  active,
  handleToggle,
  onTreatment,
  onPrevention,
  onReview,
  isAMR = false,
  disable = false,
}) => {
  const contentEl = useRef();
  const {
    title,
    content,
    upcomingFollowUp,
    isTooltip = false,
    maxClaims = "",
    allowPrevention,
    allowTreatment,
    allowReview,
    hiddenContent = "",
    // What is the condition to use this?
    isClaimTooltip = true,
  } = faq;

  const [showClaimDetails, setShowClaimDetails] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const { clinicalServiceName, clinicalServicesSmall } = useSelector(
    (state) => state.clinicalService
  );
  const { currentLoggedInUserDetail } = useSelector(
    (state) => state.currentLoggedInUserDetail
  );
  const usersState = useSelector((state) => state.users);
  const [dateRange, setDateRange] = useState(null);
  const [claimList, setClaimList] = useState([]);

  // derive AMR PIN from configured options (keeps in sync with Documentation tab)
  const amrPin = (() => {
    const opt = AMRBillingOptions.find((o) => o.value === "amrReviewPIN");
    const labelText = getLabelText(opt?.label);
    if (!labelText) return "-";
    const match = labelText.match(/PIN\s*(\d+)/i);
    return match?.[1] || "-";
  })();

  // Pharmacist name 
  const getFullName = (u) =>
    u?.firstName && u?.lastName ? `${u.firstName} ${u.lastName}` : null;
  const pharmacistName =
    getFullName(currentLoggedInUserDetail) ??
    getFullName(usersState?.currentLoggedInUserDetail) ??
    "-";

  useEffect(() => {
    document
      .querySelectorAll(".capitalize-custom")
      .forEach(capitalizeCustomText);
  }, []);

  useEffect(() => {
    document
      .querySelectorAll(".capitalize-custom")
      .forEach(capitalizeCustomText);
  }, [dateRange]);

  useEffect(() => {
    setClaimData(dateRange ?? "last4Weeks");
  }, [clinicalServicesSmall, dateRange]);

  const setClaimData = (filterOption) => {
    const now = new Date();
    let filterDate;

    switch (filterOption) {
      case "last4Weeks":
        filterDate = new Date(now.setDate(now.getDate() - 28)); // 4 weeks
        break;
      case "last3Months":
        filterDate = new Date(now.setMonth(now.getMonth() - 3));
        break;
      case "last6Months":
        filterDate = new Date(now.setMonth(now.getMonth() - 6));
        break;
      case "last12Months":
        filterDate = new Date(now.setFullYear(now.getFullYear() - 1));
        break;
      default:
        filterDate = new Date(0); // fallback: return all
        break;
    }

    if (isAMR) {
      setClaimList(
        clinicalServicesSmall?.medicationReviews
          ?.filter((f) => new Date(f.date) >= filterDate)
          .map((x) => ({
            Date: formatToYYYY_MMM_DD(x.date),
            RxDetails: "amrReviewPIN",
            ReviewType:
              x.medicationReviewTypeCode ===
              ClinicalServiceTypes.MedicationReview
                ? "Annual Medication Review"
                : "",
            PIN: amrPin,
            Location: x.location,
          }))
      );
    } else {
      setClaimList(
        clinicalServicesSmall?.minorAilments
          ?.filter((f) => new Date(f.date) >= filterDate)
          .map((x) => ({
            Date: formatToYYYY_MMM_DD(x.date),
            RxDetails: x.rxDetails,
            ReviewType: "",
            PIN: x.pin,
            Location: x.location,
          }))
      );
    }
  };

  return (
    <>
      {active !== id && (
        <div
          className={`px-5 py-4 text-sm font-normal text-gray-700 border-gray-100 border-b ${
            disable ? "bg-gray-10 cursor-not-allowed" : "hover:bg-blue-100"
          }`}
        >
          <div
            className={`flex items-start cursor-pointer duration-300 ${
              disable && "bg-gray-10 pointer-events-none"
            }`}
            onClick={() => handleToggle(id)}
          >
            <h5
              className={`duration-300 capitalize-custom text-gray-800 text-body-md pr-2`}
            >
              {" "}
              {title}{" "}
            </h5>
          </div>
          <div
            ref={contentEl}
            className={`relative overflow-hidden transition duration-300 ease-in h-0 
            ${active === id ? "!h-auto" : ""}`}
            style={{ height: "0px" }}
          >
            <div className="flex-auto min-h-[1px] pt-4">
              <div className="max-w-[800px]">{content}</div>
            </div>
          </div>
        </div>
      )}
      {active === id && (
        <>
          {/* header section */}
          <div
            className={`px-5 py-4 text-sm bg-blue-100 font-normal text-teal-600`}
          >
            <div
              className={`flex items-start cursor-pointer duration-300`}
              onClick={() => handleToggle(id)}
            >
              <h5 className="duration-300 pr-2 text-gray-800 text-body-md font-semibold">
                {title}
              </h5>
              {isTooltip && (
                <>
                  <ReactSVG
                    src="/icons/information-dark.svg"
                    className="w-5"
                    data-tooltip-id="guideline-update-1"
                  />

                  <NewToolTip
                    id="guideline-update-1"
                    render={() => <GuidelineUpdate />}
                  />
                </>
              )}
            </div>
          </div>

          {/* description section */}
          <div className={`px-5 py-2 text-sm font-normal text-gray-700`}>
            <div
              ref={contentEl}
              className={`relative overflow-hidden transition duration-300 ease-in h-0 ${
                active === id ? "!h-auto" : ""
              }`}
              style={{ height: contentEl?.current?.scrollHeight }}
            >
              <div className="flex flex-row justify-between min-h-[1px]">
                {!showMoreDetails && (
                  <>
                    <div className="text-body-md max-w-[800px]">{content}</div>
                    <div
                      onClick={() => {
                        setShowMoreDetails(true);
                      }}
                    >
                      {isAMR && (
                        <ChevronDownIcon className="w-4 h-4 text-teal-600" />
                      )}
                    </div>
                  </>
                )}
                {showMoreDetails && (
                  <>
                    <div>{content}</div>
                    <div
                      onClick={() => {
                        setShowMoreDetails(false);
                      }}
                    >
                      {isAMR && (
                        <ChevronUpIcon className="w-4 h-4 text-teal-600" />
                      )}
                    </div>
                  </>
                )}
              </div>
              {showMoreDetails && (
                <div className="flex-auto min-h-[1px] pb-5">
                  <br />
                  <div>{hiddenContent}</div>
                </div>
              )}
            </div>
          </div>
          <div>{upcomingFollowUp}</div>

          {/* eligibility section */}
          {
            <>
              <div className="flex flex-row justify-between px-5 py-2 bg-blue-100 font-normal">
                <div className="flex flex-row">
                  <h5 className="duration-300 font-normal pr-2 text-teal-300 text-body-md">
                    <span className="font-bold">Eligibility:</span>
                    &nbsp;&nbsp;Maximum Claims per Year: {maxClaims}{" "}
                    <span className="pl-20">
                      Submitted Claims: {claimList.length}
                    </span>
                  </h5>
                  {isClaimTooltip && isAMR && (
                    <>
                      <ReactSVG
                        src="/icons/information-dark.svg"
                        className="w-5"
                        data-tooltip-id="guideline-update"
                      />

                      <NewToolTip
                        id="guideline-update"
                        render={() => <MedsCheck />}
                        clickable={true}
                      />
                    </>
                  )}
                </div>

                <div className="flex justify-end items-end">
                  {!showClaimDetails && (
                    <button
                      className="flex items-center gap-2"
                      onClick={() => setShowClaimDetails(true)}
                    >
                      <h3 className="text-body-md font-bold text-teal-300">
                        View Details
                      </h3>
                      <ChevronDownIcon className="w-4 h-4 text-teal-600" />
                    </button>
                  )}
                  {showClaimDetails && (
                    <button
                      className="flex items-center gap-2"
                      onClick={() => setShowClaimDetails(false)}
                    >
                      <h3 className="text-body-md font-bold text-teal-600">
                        View Details
                      </h3>
                      <ChevronUpIcon className="w-4 h-4 text-teal-600" />
                    </button>
                  )}
                </div>
              </div>
              {isAMR && (
                <Alert
                  title={
                    <>
                      <span className="font-semibold">
                        Eligibility for Medication Review: 
                      </span>
                      <span className="font-normal">
                        &nbsp;May not apply if service was completed within the past
                        12 months.
                      </span>
                    </>
                  }
                  type="warning"
                  className="mb-4 border-l-0 border-r-0 rounded-none text-gray-800"
                  isWrapped={true}
                  compact={true}
                  // handleClose={() => setInactivePatientAlert(false)}
                  hideCross={true}
                />
              )}
              {!isAMR && claimList.length >= maxClaims && (
                <Alert
                  title={(<>
                      <span className="font-semibold text-teal-600">
                        Alert: 
                      </span>
                      <span className="font-normal text-teal-600">
                        &nbsp;Maximum number of claims for Allergic Rhinitis has been reached for {
                    claimList.length > 0
                      ? new Date(claimList[0]?.Date).getFullYear()
                      : new Date()?.getFullYear()
                  }.
                      </span>
                    </>)
                    }
                  type="warning"
                  className="mb-4 border-l-0 border-r-0 rounded-none"
                  isWrapped={true}
                  compact={true}
                  // handleClose={() => setInactivePatientAlert(false)}
                  hideCross={true}
                />
              )}
            </>
          }
          {/* claims table section */}
          {showClaimDetails && (
            <>
              <div className="relative w-44 mt-3 ml-3">
                <select
                  className="block appearance-none w-full bg-white border border-gray-100 hover:border-gray-100 px-4 pr-8"
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <option value="last4Weeks">Last 4 weeks</option>
                  <option value="last3Months">Last 3 months</option>
                  <option value="last6Months">Last 6 months</option>
                  <option value="last12Months">Last 12 months</option>
                  <option value="custom" disabled>
                      Custom Range
                  </option>
                </select>
              </div>

              <div className="overflow-hidden">
                <table className="min-w-full divide-y mx-4">
                  <thead className="text-gray-900">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-2 text-sm font-semibold text-left"
                      >
                        Date
                      </th>
                      {!isAMR && (
                        <th
                          scope="col"
                          className="px-6 py-2 text-sm font-semibold text-left"
                        >
                          Rx Details
                        </th>
                      )}
                      {isAMR && (
                        <th
                          scope="col"
                          className="px-6 py-2 text-sm font-semibold text-left"
                        >
                          Medication Review Type
                        </th>
                      )}
                      <th
                        scope="col"
                        className="px-6 py-2 text-sm font-semibold text-left"
                      >
                        ID PIN
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-2 text-sm font-semibold text-left"
                      >
                        Location
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-2 text-sm font-semibold text-left"
                      >
                        Pharmacist
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700 divide-y">
                    {(!claimList || claimList.length === 0) ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-4 text-sm text-center text-gray-600 border-b border-b-gray-100">
                          <div>No Eligibility Information Found</div>
                        </td>
                      </tr>
                    ) : (
                      claimList.map((item, index) => (
                        <tr className="transition-colors duration-300 cursor-pointer group hover:bg-blue-100">
                          <td className="px-6 py-2 text-sm whitespace-nowrap">
                            {item.Date}
                          </td>
                          {!isAMR && (
                            <td className="px-6 py-2 text-sm whitespace-nowrap">
                              {ARBillingOptions.filter(
                                (x) => x.value === item.RxDetails
                              )[0]?.info}
                            </td>
                          )}
                          {isAMR && (
                            <td className="px-6 py-2 text-sm whitespace-nowrap">
                              {item.ReviewType}
                            </td>
                          )}
                          <td className="px-6 py-2 text-sm whitespace-nowrap">
                            {isAMR
                              ? item.PIN
                              : ARBillingOptions.filter(
                                  (x) => x.value === item.RxDetails
                                )[0]?.pin}
                          </td>
                          <td className="px-6 py-2 text-sm whitespace-nowrap">
                            {item.Location}
                          </td>
                          <td className="px-6 py-2 text-sm whitespace-nowrap">
                            {pharmacistName}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="border-b border-gray-100 mx-4"></div>
            </>
          )}

          {/* take action section */}
          <div className="grid grid-cols-12 gap-3.5 relative mx-14 my-3">
            {allowPrevention && (
              <>
                <div data-tooltip-id={`prevention-tooltip-${id}`} className="col-span-6">
                  <Button
                    type={"primary-outline"}
                    text="Prevention"
                    className="!px-2.5 !py-3.5 w-full"
                    onClick={() => onPrevention(id)}
                  />
                </div>
                <NewToolTip
                  id={`prevention-tooltip-${id}`}
                  place="top"
                  render={() => (
                    <div className="flex flex-col max-w-md p-2 bg-gray-20">
                      <p className="font-normal text-left text-teal-300 text-body-sm">
                        Coming Soon
                      </p>
                    </div>
                  )}
                />
              </>
            )}
            {allowTreatment && (
              <Button
                type={"primary-outline"}
                text="Treatment"
                className="col-span-6 !px-2.5 !py-3.5"
                onClick={() => onTreatment(id)}
              />
            )}
            {allowReview && showClaimDetails && (
              <div className="col-span-12 flex flex-col items-center gap-3">
                {isClaimTooltip && (
                  <>
                    <button
                      type="button"
                      className="w-full max-w-lg !px-2.5 !py-3.5 bg-white border border-gray-100 text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600 rounded-[4px] transition-all duration-300 text-body-md font-semibold flex items-center justify-center"
                      data-tooltip-id={`add-claim-tooltip-${id}`}
                    >
                      Add a Claim
                    </button>
                    <NewToolTip
                      id={`add-claim-tooltip-${id}`}
                      place="top"
                      render={() => (
                        <div className="flex flex-col max-w-md p-2 bg-gray-20">
                          <p className="font-normal text-left text-teal-300 text-body-sm">
                            Coming Soon
                          </p>
                        </div>
                      )}
                    />
                  </>
                )}
                <div className="flex flex-wrap justify-center items-center gap-1 px-2 text-center max-w-2xl">
                  <p className="text-sm text-red-200">
                    * Claims info may not be up-to-date. See Provincial
                    Clinical Viewer.
                  </p>
                  {isClaimTooltip && (
                    <ReactSVG
                      src="/icons/information-dark.svg"
                      className="w-5 shrink-0"
                      data-tooltip-id="clinical-inf"
                      data-tooltip-place="bottom-full"
                    />
                  )}
                  <NewToolTip
                    id="clinical-inf"
                    render={() => <Clinical />}
                  />
                </div>
                <hr className="w-full border-0 border-t border-gray-100" />
                <Button
                  type={"primary-outline"}
                  text="Start Medication Review"
                  className="w-full max-w-lg !px-2.5 !py-3.5"
                  onClick={() => onReview(id)}
                />
              </div>
            )}
            {allowReview && !showClaimDetails && (
              <Button
                type={"primary-outline"}
                text="Start Medication Review"
                className="col-span-6 col-start-4 !px-2.5 !py-3.5"
                onClick={() => onReview(id)}
              />
            )}
          </div>
          <div className="border-b border-gray-100 last:border-none"></div>
        </>
      )}
    </>
  );
};

export default AccordionItem;
